import { useState, useEffect } from 'react';

// The backlog beside the board (the director, 20 Oct 2026: Week 45 B came in
// two weeks before its delivery, the board was full for two months and the
// planner could not fit it -- "Backlog sidebar", and: "all jobs that could not
// be scheduled for whatever reason should be placed on backlog as well, with a
// hover over option to display why it is on backlog and not on the board").
//
// Every line of an open order the board does not cover (GET /orders/backlog),
// soonest cut-off first, with why it is not on the board. A line is dragged
// onto a machine's day -- or picked, then a day clicked -- and the job form
// opens with it filled in, saying what saving would push.
//
// lines: from the server; daysOn(line, machineId): estimated working days;
// partOf(id), machineName(id); onPick(line) starts placing it; placingId: the
// line being placed.

const TEAL = '#14b8a6', AMBER = '#f59e0b', RED = '#ef4444', DIM = '#9ca3af', MUTED = '#6b7280', TEXT = '#f0f2f5';

const nf = (n) => Math.round(Number(n) || 0).toLocaleString('en-GB');
const day = (iso) => (iso ? new Date(iso + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : null);
const daysTo = (iso) => (iso ? Math.round((new Date(iso + 'T12:00:00') - new Date(new Date().toISOString().slice(0, 10) + 'T12:00:00')) / 86400000) : null);
const shortOrder = (n) => { const m = String(n || '').match(/^\s*week\s*(.+)$/i); return m ? `W${m[1].replace(/\s+/g, ' ')}` : (n || 'Order'); };

export default function BacklogPanel({ open, onClose, lines, error, daysOn, partOf, machineName, onPick, placingId, canPlace, focusIds = [] }) {
  const [hover, setHover] = useState(null);   // { line, rect } for the why card
  // Opened from a review or an order line: the first of those lines in view.
  const focusKey = focusIds.join(',');
  useEffect(() => {
    if (!open || !focusKey || !lines) return;
    const el = document.querySelector('[data-backlog-focus="1"]');
    if (el) el.scrollIntoView({ block: 'center' });
  }, [open, focusKey, lines]);
  if (!open) return null;

  // Grouped by order, the order's soonest first (the list comes sorted).
  const groups = [];
  for (const l of lines || []) {
    let g = groups.find(x => x.order.id === l.order.id);
    if (!g) { g = { order: l.order, lines: [] }; groups.push(g); }
    g.lines.push(l);
  }

  return (
    <div role="complementary" aria-label="Backlog"
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(360px, 100%)', zIndex: 1050, background: '#161b22', borderLeft: '1px solid #2a3040',
               boxShadow: '-12px 0 40px rgba(0,0,0,0.45)', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui', color: TEXT }}>
      <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #2a3040', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>Backlog <span style={{ color: MUTED, fontWeight: 600 }}>· {(lines || []).length}</span></div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 3, lineHeight: 1.45 }}>
            Not on the board yet. {canPlace ? 'Drag a line onto a day of a highlighted machine, or pick it and click a day: the job form opens filled in and says what it would push.' : 'Only a director places them.'}
          </div>
        </div>
        <button onClick={onClose} aria-label="Close the backlog" style={{ background: 'none', border: 'none', color: DIM, fontSize: 18, cursor: 'pointer' }}>✕</button>
      </div>

      <div style={{ overflowY: 'auto', flex: 1, padding: '8px 12px 24px' }}>
        {error && <div style={{ fontSize: 13, color: AMBER, margin: '8px 4px' }}>{error}</div>}
        {lines && !lines.length && !error && (
          <div style={{ fontSize: 13, color: DIM, padding: '16px 4px', lineHeight: 1.5 }}>
            <b style={{ color: TEXT }}>Nothing in the backlog.</b> Every line of every open order is made, from stock or on the board.
          </div>
        )}
        {!lines && !error && <div style={{ fontSize: 13, color: MUTED, padding: '16px 4px' }}>Reading the open orders…</div>}

        {groups.map(g => {
          const left = daysTo(g.order.cutoff);
          const tone = left == null ? MUTED : left < 0 ? RED : left <= 14 ? AMBER : DIM;
          return (
            <section key={g.order.id} style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '4px 4px 6px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 800 }}>{g.order.number}</span>
                {g.order.customer && <span style={{ fontSize: 11.5, color: MUTED }}>{g.order.customer}</span>}
                <span style={{ fontSize: 11.5, color: tone, marginLeft: 'auto' }}>
                  {g.order.cutoff
                    ? `cut-off ${day(g.order.cutoff)}${left < 0 ? ' · past' : left <= 14 ? ` · ${left} day${left === 1 ? '' : 's'}` : ''}`
                    : g.order.intent === 'hold' ? 'no date · on hold' : 'no date'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {g.lines.map(l => {
                  const part = partOf(l.part_id);
                  const primary = part?.primary_machine_id;
                  const est = primary ? daysOn(l, primary) : null;
                  const placing = placingId === l.line_id;
                  const focused = focusIds.includes(l.line_id);
                  const why = (l.why || []).join(' ');
                  return (
                    <div key={l.line_id} role="button" tabIndex={canPlace ? 0 : -1} data-backlog-focus={focused ? '1' : undefined}
                      draggable={!!canPlace && !!part}
                      onDragStart={e => { e.dataTransfer.setData('text/plain', `backlog:${l.line_id}`); e.dataTransfer.effectAllowed = 'move'; setHover(null); onPick(l); }}
                      onClick={() => { if (canPlace && part) onPick(l); }}
                      onKeyDown={e => { if (e.key === 'Enter' && canPlace && part) onPick(l); }}
                      onMouseEnter={e => setHover({ line: l, rect: e.currentTarget.getBoundingClientRect() })}
                      onMouseLeave={() => setHover(h => (h && h.line.line_id === l.line_id ? null : h))}
                      style={{ background: placing ? 'rgba(20,184,166,0.12)' : focused ? 'rgba(245,158,11,0.08)' : '#1e2329', border: `1px solid ${placing ? TEAL + '88' : focused ? AMBER + '99' : '#2a3040'}`, borderRadius: 9, padding: '9px 11px',
                               cursor: canPlace && part ? 'grab' : 'default', userSelect: 'none' }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                        <i className="fa-solid fa-grip-vertical" style={{ color: MUTED, fontSize: 10, flexShrink: 0 }} aria-hidden="true" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>
                            {part?.name || `Part #${l.part_id}`}
                            {/* Which order it is for, on the card itself (20 Oct 2026). */}
                            <span title={`For ${l.order.number}${l.order.customer ? ` (${l.order.customer})` : ''}`}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 7, verticalAlign: 1, whiteSpace: 'nowrap',
                                       fontSize: 10.5, fontWeight: 700, color: '#fbbf24', background: 'rgba(251,191,36,0.10)',
                                       border: '1px solid rgba(251,191,36,0.3)', borderRadius: 6, padding: '1px 6px' }}>
                              <i className="fa-solid fa-truck" style={{ fontSize: 9 }} />{shortOrder(l.order.number)}
                            </span>
                          </div>
                          {l.product && <div style={{ fontSize: 11.5, color: TEAL }}>{l.product}</div>}
                        </div>
                        <i className="fa-solid fa-circle-question" style={{ color: MUTED, fontSize: 12, flexShrink: 0 }} title={why} aria-label={`Why it is in the backlog: ${why}`} />
                      </div>
                      <div style={{ fontSize: 12, color: DIM, marginTop: 4, marginLeft: 18 }}>
                        <b style={{ color: TEXT }}>{nf(l.missing)} pcs</b>{l.missing !== l.quantity ? ` of ${nf(l.quantity)}` : ''}
                        {est ? ` · about ${est} day${est === 1 ? '' : 's'} on ${machineName(primary)}` : part && !part.target_cycle_s ? ' · no cycle time: days unknown' : ''}
                      </div>
                      {placing && <div style={{ fontSize: 11.5, color: TEAL, marginTop: 4, marginLeft: 18 }}>Placing: drop it or click a day on a highlighted machine · Esc to stop</div>}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {/* Why it is here: on hover, beside the line. */}
      {hover && (
        <div style={{ position: 'fixed', top: Math.min(hover.rect.top, window.innerHeight - 220), right: 'min(372px, 100%)', width: 300, zIndex: 1051,
                      background: '#0b0e12', border: '1px solid #2a3040', borderRadius: 9, padding: '10px 12px', boxShadow: '0 10px 30px rgba(0,0,0,0.55)',
                      fontSize: 12.5, lineHeight: 1.5, color: '#d1d5db', pointerEvents: 'none' }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: AMBER, marginBottom: 4 }}>
            Why {shortOrder(hover.line.order.number)} is in the backlog
          </div>
          {(hover.line.why || []).map((w, i) => <div key={i}>{w}</div>)}
          <div style={{ color: MUTED, marginTop: 6, fontSize: 11.5 }}>
            Ordered {nf(hover.line.quantity)}{hover.line.made ? ` · made ${nf(hover.line.made)}` : ''}{hover.line.stock ? ` · from stock ${nf(hover.line.stock)}` : ''}{hover.line.planned ? ` · on the board ${nf(hover.line.planned)}` : ''} · missing {nf(hover.line.missing)}
          </div>
        </div>
      )}
    </div>
  );
}
